import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {UserRepository} from "../repositories/user.repository";
import {User} from "../interfaces/rest/User";
import {GlobalConfig} from "../configs/GlobalConfig";


// declare let ga: Function;
declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {

  private currentUrl: string = '';

  private interactionStates = {
    clicked: 0,
  };

  constructor(private userSessionRepository: UserSessionRepository) {

  }

  customEvent(eventName: string, eventObject: any) {
    if (!gtag) {
      console.warn('No gtag function');
    }
    else {
      try {
        if(eventObject) {
          eventObject['link_url'] = this.currentUrl;

          const userSession = this.userSessionRepository.getUserSession();
          if(userSession) {
            eventObject['link_env_'] = GlobalConfig.environment;
            eventObject['link_version_'] = GlobalConfig.version;
            eventObject['link_build_'] = GlobalConfig.buildNumber;

            eventObject['link_userEmail_'] = userSession.email;
            eventObject['link_userId_'] = userSession.userId;
            eventObject['link_userName_'] = `${userSession.firstName} ${userSession.lastName}`;
            eventObject['link_userInternal_'] = userSession.userType === 'see';
            eventObject['link_userLanguage_'] = userSession.languageCode;
            eventObject['link_userRole_'] = userSession.roles ? userSession.roles[0] : 'unknown';

            if(userSession.userType !== 'see') {
              eventObject['link_userClientId_'] = userSession.clientId;
            }
          }
        }

      }
      catch(e) {
        console.error(`GA error`, e);
      }

      console.log(`Recording GA event: ${eventName}`);

      gtag('event', eventName, eventObject);
    }
  }

  pageView(url: string): void {
    this.currentUrl = url;

    this.customEvent('page_view', {
      'event_category': 'navigation',
      'event_label': 'User viewed page',
    });
  }

  pageInteraction(eventTarget: EventTarget|null,
                  eventCategory: string,
                  eventLabel: string,
                  additionalEventData: any = null): boolean {
    if(eventTarget) {
      const gaNodes = this.getGaNodes(eventTarget);

      if(gaNodes && gaNodes.length) {
        const eventName = this.generateEventName(gaNodes);
        const eventDetails = this.generateEventDetails(gaNodes);

        const eventObject: { [key: string]: any } = {};
        eventObject['event_category'] = eventCategory;
        eventObject['event_label'] = eventLabel;

        for(let key in eventDetails) {
          eventObject['link_' + key] = eventDetails[key];
        }

        if(additionalEventData) {
          for(let key in additionalEventData) {
            eventObject['link_' + key] = additionalEventData[key];
          }
        }

        this.customEvent(eventName, eventObject);

        return true;
      }
    }

    return false;
  }

  trackClick(event: MouseEvent): void {
    this.pageInteraction(event.target, 'click', 'User clicked element');
  }

  trackTouchEnd(event: TouchEvent): void {
    this.pageInteraction(event.target, 'touch', 'User touched element');
  }

  trackKeyUp(event: KeyboardEvent): void {
    this.pageInteraction(event.target, 'keyboard', 'User entered input');
  }

  protected getGaNodes(eventTarget: EventTarget): Array<GaNode> {
    let hasRealNode = false;
    const nodes: Array<GaNode> = [];

    if(eventTarget instanceof HTMLElement || eventTarget instanceof SVGElement) {
      let currentElement: any = eventTarget;

      while(currentElement) {
        let isRoot = false;
        let nodeName: string = currentElement.getAttribute('data-link-ga-node');
        let nodeDetails = {};

        if(nodeName) {
          hasRealNode = true;
        }
        else {
          nodeName = currentElement.getAttribute('data-link-ga-root');

          if(nodeName) {
            isRoot = true;
          }
        }

        if(nodeName) {
          nodeName = nodeName.replaceAll('t.', '');
          nodeName = nodeName.replaceAll('.', '_');
          nodeName = nodeName.replaceAll(' ', '_');

          const rawNodeDetails = currentElement.getAttribute('data-link-ga-details');
          if(rawNodeDetails && rawNodeDetails.length > 0) {
            try {
              nodeDetails = JSON.parse(rawNodeDetails);
            }
            catch(e) {
              // ignore
            }
          }

          nodes.push({
            name: nodeName,
            details: nodeDetails,
          });
        }

        if(isRoot) {
          break;
        }

        currentElement = currentElement.parentElement;
      }
    }
    else {
      console.log(`Unknown element is clicked`);
    }

    if(hasRealNode) {
      return nodes.reverse();
    }

    return [];
  }

  protected generateEventName(gaNodes: Array<GaNode>): string {
    let eventName = '';
    gaNodes.forEach((gaNode) => {
      if(eventName.length > 0) {
        eventName = eventName + '_';
      }

      eventName = eventName + gaNode.name;
    });

    return eventName;
  }

  protected generateEventDetails(gaNodes: Array<GaNode>): { [key: string]: any } {
    const eventDetails: { [key: string]: any } = {};
    gaNodes.forEach((gaNode) => {
      if(gaNode.details) {
        for(let key in gaNode.details) {
          eventDetails[key] = gaNode.details[key];
        }
      }
    });

    return eventDetails;
  }
}

interface GaNode {

  name: string;
  details: { [key: string]: any };
}
